import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import moment from 'moment-timezone';
import { Icon, Grid, Typography, Button, Box, useMediaQuery } from '@mui/material';
import { Theme, useTheme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import AutoSizer from 'react-virtualized-auto-sizer';
import { List, CellMeasurer, CellMeasurerCache, WindowScroller } from 'react-virtualized';
import { useWindowSize } from 'common/hooks/useWindowSize';

import { OrderInfo } from 'common/types';
import { getSearchResults } from 'common/utils/searchFunctions';
import EmptyState from 'common/components/EmptyState';
import Container from 'components/Container';
import RentalCard from './RentalCard';
import BarCodeReader from 'components/BarCodeReader';
import { ReactComponent as ReturnIcon } from 'common/assets/icons/icon-return_version-outline.svg';
import {
	ROW_TYPES,
	generateRowsForActiveRentalsView,
	ReactVirtualizedRowForRentals,
} from './rentalsViewReactVirtualizedUtils';
import { RentalsTabValues } from 'services/types';

import { Routes, push } from 'routing';

import { isBookingsViewDeliveryChecked } from 'actions';

import {
	buildRentalsOfTypeLoading,
	buildRentalsOfTypeAtSelectedLocation,
	rentalsAtSelectedLocationWithDeliveryFrom,
	RentalsType,
	LocationType,
} from 'selectors/RentalsSelectors';
import * as StockSelectors from 'selectors/StockSelectors';
import * as ShopSelectors from 'selectors/ShopSelectors';
import * as ViewSelectors from 'selectors/ViewSelectors';
import { localFormatString } from 'common/utils/dateUtils';
import HasFeature from 'components/features/HasFeature';
import { useTranslation } from 'services/localization/useTranslation';
import RentalSearchFilter from './RentalSearchBar/RentalSearchFilter';

const cache = new CellMeasurerCache({
	minHeight: 60,
	fixedWidth: true,
});

const rentalsSelector = buildRentalsOfTypeAtSelectedLocation(RentalsType.ACTIVE, LocationType.END);

const deliveryRentalsSelector = rentalsAtSelectedLocationWithDeliveryFrom(
	RentalsType.ACTIVE,
	LocationType.END,
);

const rentalsLoadingSelector = buildRentalsOfTypeLoading(RentalsType.ACTIVE);

const ActiveRentalsView = () => {
	const { t } = useTranslation();
	const classes = useStyles();
	const dispatch = useDispatch();

	const windowSize = useWindowSize();
	const theme = useTheme();
	const isMobileScreenSize = useMediaQuery(theme.breakpoints.down('sm'));
	const deliveryRentals = useSelector(deliveryRentalsSelector);
	const rentals = useSelector(rentalsSelector);
	const rentalsLoading = useSelector(rentalsLoadingSelector);
	const stockProducts = useSelector(StockSelectors.stockProductsData);
	const categories = useSelector(StockSelectors.categoriesData);
	const bookingsViewDeliveryChecked = useSelector(ViewSelectors.bookingsViewDeliveryChecked);
	const shopDateFormat = useSelector(ShopSelectors.getShopDateFormat);
	const locationFilterType = useSelector(ViewSelectors.bookingsViewLocationType);
	const dateFormatString = localFormatString('DD.MM.', shopDateFormat);
	const [searchInput, setSearchInput] = useState('');
	const [filteredRentals, setFilteredRentals] = useState<null | OrderInfo[]>(null);
	const [reactVirtualizedRows, setReactVirtualizedRows] = useState<ReactVirtualizedRowForRentals[]>(
		[],
	);

	const rentalsForSearch = useMemo(() => {
		return bookingsViewDeliveryChecked ? deliveryRentals : rentals;
	}, [deliveryRentals, rentals, bookingsViewDeliveryChecked]);

	const rentalsToRender = useMemo(() => {
		const rentalsToUse = filteredRentals ?? rentalsForSearch;
		return !!searchInput
			? getSearchResults(searchInput, rentalsToUse, ['NAME', 'PRODUCT_CODE', 'ORDER_BARCODE'])
			: rentalsToUse;
	}, [filteredRentals, rentalsForSearch, searchInput]);

	useEffect(() => {
		cache.clearAll();
	}, [windowSize]);

	useEffect(() => {
		cache.clearAll();
		const activeRentalRows = generateRowsForActiveRentalsView(rentalsToRender);
		setReactVirtualizedRows(activeRentalRows);
	}, [rentalsToRender]);

	const handleBarCodeRead = (input: string) => {
		const searchResults = getSearchResults(input, rentals, [
			'NAME',
			'PRODUCT_CODE',
			'ORDER_BARCODE',
		]);
		if (searchResults.length === 1) {
			push(Routes.BookingPage(searchResults[0].id));
			return;
		}
		setSearchInput(input);
	};

	const handleToggleDelivery = () => {
		dispatch(isBookingsViewDeliveryChecked(!bookingsViewDeliveryChecked));
	};

	const openReturnView = () => {
		push(Routes.RETURN);
	};

	const renderFilterResultInfo = () => {
		return (
			<div className={classes.searchResultsContainer}>
				<Typography variant="h6">
					{rentalsToRender.length} {t('rentals.amountOfRentals', 'Rentals were found')}
				</Typography>
			</div>
		);
	};

	const renderCardLabels = (dateStringAsMoment: moment.Moment) => {
		const formattedDateString = dateStringAsMoment.isSame(moment(), 'day')
			? dateStringAsMoment.format(`[${t('common:times.today')},] ${dateFormatString}`)
			: dateStringAsMoment.format(dateFormatString);
		return (
			<Grid
				container
				alignItems="flex-end"
				justifyContent="flex-end"
				className={classes.cardLabelPadding}
			>
				<Grid item xs={6}>
					<Typography variant="h5">{formattedDateString}</Typography>
				</Grid>
				<Grid item xs={6} className={classes.nextReturnTime}>
					<Typography color="textSecondary" variant="caption">
						{t('rentals.nextReturnTime', 'Next return time:')}
					</Typography>
				</Grid>
			</Grid>
		);
	};

	const renderRentalCard = (rental: OrderInfo, index: number) => {
		return (
			<RentalCard
				key={`${rental.id}${index}`}
				linkTo={Routes.BookingPage(rental.id)}
				rentalInfo={rental}
				rentalTab={RentalsTabValues.ACTIVE}
				stockProducts={stockProducts}
				categories={categories}
				showLocations={locationFilterType === 'all'}
				delivery={rental?.services?.delivery}
			/>
		);
	};

	const renderNoRentalsToday = () => (
		<Typography variant="body1" align="center">
			{t('rentals.noBookingsToday2', 'No orders for today')}
		</Typography>
	);

	const rowRenderer = ({
		index,
		key,
		style,
		parent,
	}: {
		index: number;
		key: string;
		style: any;
		parent: any;
	}) => {
		const { DATE_TEXT, RENTAL_CARD, NO_RENTALS_TODAY } = ROW_TYPES;
		const rowToRender: ReactVirtualizedRowForRentals = reactVirtualizedRows[index];
		const isLastRow = index === reactVirtualizedRows.length - 1;

		return (
			<CellMeasurer cache={cache} columnIndex={0} key={key} parent={parent} rowIndex={index}>
				<div style={style}>
					<Container
						className={isLastRow ? classes.reactVirtualizedLastRow : classes.reactVirtualizedRow}
						fluid
					>
						{rowToRender.type === DATE_TEXT ? (
							renderCardLabels(rowToRender.date)
						) : rowToRender.type === RENTAL_CARD ? (
							renderRentalCard(rowToRender.data, index)
						) : rowToRender.type === NO_RENTALS_TODAY ? (
							renderNoRentalsToday()
						) : (
							<div>{'ROW TYPE NOT DEFINED'}</div>
						)}
					</Container>
				</div>
			</CellMeasurer>
		);
	};

	return (
		<EmptyState
			title={t('rentals.empty_states.activeEmpty2', 'You have no active orders')}
			description={t(
				'rentals.empty_states.activeEmptyDescription2',
				`Once you've started an order, you'll be able to see and manage your active orders here.`,
			)}
			isEmpty={rentals.length === 0}
			isLoading={rentalsLoading}
			render={() => (
				<BarCodeReader barCodeRead={(p) => handleBarCodeRead(p)}>
					<Container className={classes.main} fluid>
						<HasFeature requiredFeature="MASS_RETURN">
							<Box display="flex" justifyContent="flex-end" mb={2}>
								<Button
									variant="contained"
									color="primary"
									size={isMobileScreenSize ? 'small' : 'medium'}
									className={classes.btn}
									onClick={openReturnView}
								>
									{t('rentals.returnItems', 'Return products')}
									<Icon
										color="inherit"
										fontSize={isMobileScreenSize ? 'small' : 'medium'}
										className={classes.returnIcon}
									>
										<ReturnIcon />
									</Icon>
								</Button>
							</Box>
						</HasFeature>
						<Box mb={1} mt={1}>
							<RentalSearchFilter
								rentals={rentalsForSearch}
								onFilteredRentalsChange={(newFilteredRentals: null | OrderInfo[]) =>
									setFilteredRentals(newFilteredRentals)
								}
								searchInput={searchInput}
								onSearchInputChange={setSearchInput}
								onToggleDelivery={handleToggleDelivery}
								deliveryRentalsCount={deliveryRentals.length}
								mode="active"
							/>
						</Box>
						{(!!searchInput || filteredRentals) && renderFilterResultInfo()}
					</Container>
					<div className={classes.reactVirtualizedContainer}>
						<WindowScroller>
							{({ height, onChildScroll, scrollTop }) => (
								<AutoSizer disableHeight>
									{({ width }) => (
										<List
											style={{ outline: 'none' }}
											autoHeight
											deferredMeasurementCache={cache}
											width={width}
											height={height}
											rowCount={reactVirtualizedRows.length}
											rowHeight={cache.rowHeight}
											rowRenderer={rowRenderer}
											scrollTop={scrollTop}
											onScroll={onChildScroll}
											overscanRowCount={2}
										/>
									)}
								</AutoSizer>
							)}
						</WindowScroller>
					</div>
				</BarCodeReader>
			)}
		/>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		'@global': {
			html: {
				overflowY: 'scroll',
				scrollbarWidth: 'none',
				'-ms-overflow-style': 'none',
			},
			'html::-webkit-scrollbar': {
				width: 0,
				height: 0,
			},
		},
		main: {
			paddingTop: theme.spacing(2),
			paddingBottom: theme.spacing(1),
			flex: 0,
		},
		reactVirtualizedContainer: {
			paddingTop: theme.spacing(1),
			[theme.breakpoints.down('sm')]: {
				paddingTop: theme.spacing(0),
			},
		},
		reactVirtualizedRow: {
			paddingTop: theme.spacing(0.5),
			paddingBottom: theme.spacing(0.5),
		},
		reactVirtualizedLastRow: {
			paddingTop: theme.spacing(0.5),
			paddingBottom: theme.spacing(10),
			[theme.breakpoints.down('sm')]: {
				paddingBottom: theme.spacing(8),
			},
		},
		spacingTop: {
			marginTop: 32,
		},
		badge: {
			top: 6,
			right: 12,
		},
		searchResultsContainer: {
			display: 'flex',
			alignItems: 'center',
			marginBottom: theme.spacing(1),
			[theme.breakpoints.down('sm')]: {
				justifyContent: 'space-between',
			},
		},
		cardLabelPadding: {
			padding: '16px 16px 8px 0px',
		},
		rentalContainer: {
			paddingBottom: 16,
		},
		nextReturnTime: {
			textAlign: 'right',
		},
		btn: {
			borderRadius: 50,
			[theme.breakpoints.down('sm')]: {
				fontSize: '1rem',
				paddingTop: 0,
				paddingBottom: 0,
			},
		},
		returnIcon: {
			marginLeft: theme.spacing(1),
		},
		mobileSpacing: {
			[theme.breakpoints.down('sm')]: {
				marginTop: 16,
			},
		},
		onlyBtn: {
			float: 'right',
		},
	}),
);

export default ActiveRentalsView;
